/**
 * Guided links from the CleanCleaver team (core guided.ts; docs/TEAM-CONSOLE.md "Guided links"): `?go=add_person&gm=<message>`.
 * Like "We have this" (haveIntent.ts), the link is kept the moment the app loads (localStorage, in try/catch) so a sign-in or a
 * reload can't lose it, and opens the place once the person is in their workspace. Only ids on the core list are kept; the
 * param is a plain id, never a URL. Opening it marks the message clicked.
 */
import { guidedFromQuery } from "@logwave/core";

export const GO_KEY = "cc.go.pending";
export interface PendingGo { dest: string; param: string | null; messageId: string | null; path: string; at: string }

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
let memory: PendingGo | null = null;

export function captureGo(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): PendingGo | null {
  const g = guidedFromQuery(search);
  if (!g) return null;
  const v: PendingGo = { ...g, at: now.toISOString() };
  memory = v;
  try { storage?.setItem(GO_KEY, JSON.stringify(v)); } catch { /* private mode: this page load only */ }
  return v;
}
/** The waiting link, if it is under a day old (an old link opening by surprise days later would confuse). */
export function pendingGo(storage: Storage | undefined = safeStorage(), now = new Date()): PendingGo | null {
  let v: PendingGo | null = memory;
  try { const raw = storage?.getItem(GO_KEY); if (raw) v = JSON.parse(raw) as PendingGo; } catch { /* use memory */ }
  if (!v || now.getTime() - Date.parse(v.at) > 86_400_000) return null;
  // Re-check against the list: a stored value is never trusted as a path.
  const q = new URLSearchParams({ go: v.dest, ...(v.param ? { gp: v.param } : {}), ...(v.messageId ? { gm: v.messageId } : {}) });
  const g = guidedFromQuery(q.toString());
  return g ? { ...g, at: v.at } : null;
}
export function clearGo(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(GO_KEY); } catch { /* ignore */ }
}
export function withoutGoParams(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of ["go", "gp", "gm"]) p.delete(k);
  const s = p.toString();
  return s ? `?${s}` : "";
}
